import React, { useState, useEffect, useRef } from 'react';
import {Button, Hidden, IconButton, Typography} from '@mui/material';
import {AddBox, ChevronRightRounded} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import MaterialTable from 'material-table';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import {getMediaURL, scrollTop, showTableDate} from "../../../../config/values";
import {isAdmin, TableCount, TablePage} from "../../../../config/Permissions";
import {getDataTableLocalization} from "../../../../config/Translate";
import {withStyles} from "@mui/styles";
import {Styles} from "../../../../config/design/styles";
import {tableIcons} from "../../../../config/design/TableIcons";
import {defaultVariables} from "../../../../config";
import {useIsSmall} from "../../../../config/hooks/useWindowDimensions";
import {useTranslation} from "react-i18next";
import {Eye} from "lucide-react";
import ChevronForward from "../../../components/icons/ChevronForward";

const BSRequests = ({ classes }) => {
    const isSmall = useIsSmall();
    const { t } = useTranslation();

    const [columns] = useState([

        {
            title: t('user'),
            field: 'user',
            filterPlaceholder: t('search'),
            sorting: false,
            render: rowData => (
                <Typography>
                    {rowData.user.first_name} {rowData.user.last_name}
                </Typography>

            )
        },
        {
            title: t("bsNumber"),
            field: "type",
            render: rowData => (
                <Typography>
                    {rowData.number}
                </Typography>

            )
        },
        {
            title: t("orderType"),
            field: "type",
            lookup: {
                buy: <Typography className="table-badge active">{t('buy')}</Typography>,
                sell: <Typography className="table-badge inactive">{t('sell')}</Typography>,
            },
        },
        {
            title: t("number_of_stocks"),
            field: "number_of_stocks",
            render: rowData => (
                <Typography>
                    <strong>{rowData.number_of_stocks}</strong> / {t(rowData.stock_type)}
                </Typography>

            )
        },
        {
            title: t("stockType"),
            field: "is_complete",
            lookup: {
                1: <Typography >{t('completeStock')}</Typography>,
                0: <Typography >{t('incompleteStock')}</Typography>,
            },
        },
        {
            title: t("status"),
            field: "status",
            lookup: {
                confirmed: <Typography className="table-badge active">{t('confirmed')}</Typography>,
                pending: <Typography className="table-badge pending">{t('pending')}</Typography>,
                rejected: <Typography className="table-badge inactive">{t("rejected")}</Typography>,
            },
        },
        {
            title: t('created_at'),
            editable: 'never',
            field: 'created_at',
            render: rowData => (
                <Typography className={classes.dateTable}>
                    {showTableDate(rowData.created_at)}
                </Typography>
            )
        },
        {
            title: t('activity'),
            editable: 'never',
            field: 'activity',
            sorting: false,
            render: rowData => (
                <>
                    <Button
                        variant="outlined"
                        color="secondary"
                        component={Link}
                        to={`/bs-requests/view/${rowData.id}`}
                        endIcon={<ChevronForward />}
                        size="small"
                        style={{ margin: 5 }}
                    >
                        {t('details')}
                    </Button>
                </>

            )
        }
    ]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const tableRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const loadData = () => {
        tableRef.current && tableRef.current.onQueryChange();
    };

    const onDelete = async (id, hasNotif = true) => {
        try {
            const loginToken = localStorage.getItem('ACT');
            let data = new FormData();
            data.append('id', id);
            await axios.post(`${defaultVariables.url}/bs-requests/remove`, data, {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${loginToken}`
                }
            });
            loadData();
        } catch (error) {
            console.log(error.response)
            enqueueSnackbar(t('deleteDataFailed'), { variant: 'error' });
        }
    };

    useEffect(() => {
        loadData();
        scrollTop();
    }, []);

    return (
        <>
            {
                loading ? "Loading" :
                    <div className="main-table">
                        <MaterialTable
                            tableRef={tableRef}

                            icons={tableIcons}
                            title={
                                <>
                                {
                                    !isAdmin() &&
                                    <Link to='/bs-requests/add'>
                                        <Hidden mdDown>
                                            <Button variant="contained" color="secondary" startIcon={<AddBox />}>
                                                {t('makeRequest')}
                                            </Button>
                                        </Hidden>
                                        <Hidden mdUp>
                                            <IconButton color="primary">
                                                <AddBox />
                                            </IconButton>
                                        </Hidden>
                                    </Link>
                                }
                                </>
                            }
                            localization={getDataTableLocalization()}
                            columns={columns}
                            data={query =>
                                new Promise((resolve, reject) => {
                                    axios.get(`${defaultVariables.url}/bs-requests/quickView?page=${query.page + 1}&per_page=${query.pageSize}&search=${query.search}`, {
                                        headers: {
                                            Accept: 'application/json',
                                            Authorization: `Bearer ${localStorage.getItem('ACT')}`
                                        }
                                    }).then(response => {
                                        const data = response.data.data.map(row => ({
                                            id: row.id,
                                            number: row.number,
                                            user: row.user,
                                            status: row.status,
                                            type: row.type,
                                            stock_type: row.stock_type,
                                            is_installmental: row.is_installmental,
                                            is_complete: row.is_complete,
                                            number_of_stocks: row.number_of_stocks,
                                            created_at: row.created_at,
                                        }));
                                        resolve({
                                            data: data,
                                            page: response.data.current_page - 1,
                                            totalCount: response.data.total
                                        });
                                    }).catch(error => console.log(error.response));
                                })
                            }
                            options={{
                                pageSize: TablePage,
                                pageSizeOptions: TableCount,
                                defaultExpanded: false,
                                draggable: false,
                            }}
                            editable={!!isAdmin() && {
                                onRowDelete: oldData =>
                                    new Promise(resolve => {
                                        setTimeout(() => {
                                            resolve();
                                            onDelete(oldData.id);
                                            setData(prevData => prevData.filter(item => item.id !== oldData.id));
                                        }, 600);
                                    })
                            }}
                        />
                    </div>
            }
        </>
    );
};
export default withStyles(Styles)(BSRequests);
